import {useCallback, useEffect, useSyncExternalStore} from "react";
import {shouldPromptEmailVerification} from "../account/emailTrust";
import {autoSendVerificationEmail, sendVerificationEmail} from "../auth/authProviders";
import {
	classifyEmailVerificationSendFailure,
	type EmailVerificationSendFailure,
} from "../auth/emailVerificationSend";
import {useUser} from "../UserProvider";
import {hasAutoSentFor, recordAutoSentFor} from "./autoSendRegistry";
import {
	beginVerificationSend,
	focusVerificationSend,
	settleVerificationSend,
	subscribeVerificationSend,
	verificationSendState,
} from "./verificationSendStore";

/** Which of the two faces the callout is wearing. */
export type EmailVerificationPromptState = "sent" | "prompt";

export interface EmailVerificationPrompt {
	/** Whether the callout belongs on screen at all. */
	visible: boolean;
	/** `sent` = the check-your-inbox state; `prompt` = the one with the control. */
	state: EmailVerificationPromptState;
	/** Whether the control offers a FIRST send or a resend — a mail already went to this account. */
	action: "send" | "resend";
	/** True while a send is in flight, so the view can disable its control and say so. */
	sending: boolean;
	/** The last send's failure, or null. Success is carried by {@link state}, not by this. */
	failure: EmailVerificationSendFailure | null;
	/** The last send was refused because the address is already confirmed — an answer, not a failure. */
	alreadyVerified: boolean;
	send: () => void;
}

/**
 * React adapter for the verify-your-email callout (ui#1458).
 *
 * Thin by mandate: every decision belongs to a React-free module — who sees the callout
 * (`account/emailTrust`), whether a mail already went out (`./autoSendRegistry`), what the callout
 * currently shows (`./verificationSendStore`), what a failed send means
 * (`auth/emailVerificationSend`), how the mail is sent (`auth/authProviders`, which since ui#1477
 * asks our api for a branded message instead of letting Firebase send its stock one).
 *
 * AUTO-SEND (owner, 2026-08-23): the first time a signed-in account with an unverified address is
 * seen, the mail goes out with no click and the callout opens straight in its sent state. Every later
 * session while still unverified shows the prompt with a RESEND control. The "first time" is recorded
 * per uid BEFORE the request starts, so a re-render, a StrictMode double-effect or a second mount
 * cannot turn one automatic mail into several — and the automatic path additionally declines to mail
 * an address Firebase itself already reports as verified.
 *
 * State lives in the module store, not in `useState`, because BOTH mount points can be alive at once
 * and two instances disagreeing about whether a mail just went out is a contradiction the user can
 * see. See `./verificationSendStore`.
 */
export function useEmailVerificationPrompt(): EmailVerificationPrompt {
	const [user] = useUser();
	const sendState = useSyncExternalStore(
		subscribeVerificationSend,
		verificationSendState,
		verificationSendState,
	);
	const accountId = user.id;
	const visible = shouldPromptEmailVerification({loggedIn: user.isLoggedIn, trust: user.emailTrust});

	const send = useCallback(() => {
		beginVerificationSend(accountId);
		recordAutoSentFor(accountId);
		// One mail at a time is enforced at the `authProviders` seam, not here: React state reaches the
		// DOM a render later, so a `disabled` attribute cannot stop a second activation in the same
		// tick (ui#360). A duplicate activation JOINS the in-flight send.
		// Settles on the RETURNED outcome, not on a hardcoded "sent": since ui#1477 the api refuses an
		// already-proven address, and claiming an inbox has mail waiting in it would be the one wrong
		// thing this callout must never say.
		sendVerificationEmail()
			.then((outcome) => settleVerificationSend(accountId, outcome))
			.catch((error: unknown) =>
				settleVerificationSend(accountId, classifyEmailVerificationSendFailure(error)));
	}, [accountId]);

	useEffect(() => {
		// Reading the registry here rather than during render: it lives in `localStorage`, and an
		// external mutable store read mid-render is not one React 19 promises to keep consistent.
		focusVerificationSend(accountId);
		if (!visible || hasAutoSentFor(accountId)) {
			return;
		}
		// Recorded BEFORE the send, never after: the record is what makes this happen once, and an
		// `await` before it is a window in which a second mount reads "not sent yet" and mails again.
		recordAutoSentFor(accountId);
		beginVerificationSend(accountId);
		autoSendVerificationEmail()
			.then((outcome) => settleVerificationSend(accountId, outcome))
			.catch((error: unknown) =>
				settleVerificationSend(accountId, classifyEmailVerificationSendFailure(error)));
	}, [visible, accountId]);

	const current = sendState.accountId === accountId ? sendState : null;
	return {
		visible,
		state: current?.sent === true ? "sent" : "prompt",
		action: current?.recorded === true ? "resend" : "send",
		sending: current?.sending === true,
		failure: current?.failure ?? null,
		alreadyVerified: current?.alreadyVerified === true,
		send,
	};
}
